import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/cartContext"; // Assuming you have this context
import { useNavigate } from "react-router-dom"; // Use useNavigate instead of useHistory
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const stripePromise = loadStripe(
  "pk_test_51NDEMaHB8rLE0wX12LWngeUZjcnS2peDAoVfxzhasrTUX9EPo1Sef2XlbJgsjZjZegGsVM3obfYaWfiXGJtWGyDs00qtV3PXsA"
);

const PaymentForm = ({ finalTotal }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const amount = parseFloat(searchParams.get("amount"));
  const { cartItems, clearCart } = useCart(); // Assuming clearCart is provided by the context
  const navigate = useNavigate(); // Use useNavigate here to get the navigate function
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      console.error("Stripe.js has not loaded yet.");
      return;
    }

    setIsProcessing(true);
    const cardElement = elements.getElement(CardNumberElement);

    try {
      console.log(
        "Sending request to create PaymentIntent with amount:",
        amount
      );

      const response = await axios.post(
        "https://api.theonlysale.com/api/pay/checkout",
        {
          orderId,
          amount,
          paymentMethod: "card", // This should be 'card' as per backend
          cartItems,
        }
      );

      if (!response.data || !response.data.clientSecret) {
        console.error("No clientSecret returned from backend.");
        throw new Error("Failed to retrieve client secret.");
      }

      const clientSecret = response.data.clientSecret;

      console.log(response.data);
      console.log("clientSecret received:", clientSecret);

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: "Customer Name",
            },
          },
        }
      );

      const paymentIntentId = paymentIntent.id;

      console.log("paymentIntent:", paymentIntent);
      try {
        await axios.put(
          `https://api.theonlysale.com/api/order/${orderId}/payment-intent`,
          {
            paymentIntentId,
          }
        );
      } catch (updateError) {
        console.error("Error updating paymentIntentId:", updateError);
        setError("Payment succeeded but updating order failed.");
      }
      if (error) {
        console.error("Error during payment confirmation:", error);
        setError(`Payment failed: ${error.message}`);
      } else {
        console.log("PaymentIntent status:", paymentIntent.status);

        if (paymentIntent.status === "requires_capture") {
          console.log(
            "Payment succeeded! Sending paymentIntentId to update order:",
            paymentIntentId
          );
          console.log("Order ID from URL:", orderId); // Debugging step

          await axios.put(
            `https://api.theonlysale.com/api/order/update/status/${orderId}`,
            {
              status: "confirmed",
              products: cartItems.map((item) => ({
                id: item.id,
                quantity: item.quantity || 1, // Default to 1 if quantity is undefined
              })),
            }
          );
          setPaymentSuccess(true);
          setShowSuccessModal(true);

          setTimeout(() => {
            clearCart();
            navigate("/");
          }, 5000);
        } else if (paymentIntent.status === "requires_capture") {
          setPaymentSuccess(true);
          console.log("Payment authorized, awaiting capture.");
        } else {
          console.warn(
            "Unexpected PaymentIntent status:",
            paymentIntent.status
          );
          setError("Payment failed with an unexpected status.");
        }
      }
    } catch (err) {
      console.error("Error processing payment:", err.message);
      setError(`Error processing payment: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Card Number
          </label>
          <CardNumberElement className="border rounded-lg p-3 mt-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            Expiration Date
          </label>
          <CardExpiryElement className="border rounded-lg p-3 mt-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white">
            CVC
          </label>
          <CardCvcElement className="border rounded-lg p-3 mt-2" />
        </div>

        {error && <div className="text-red-500 text-sm">{error}</div>}
        {paymentSuccess && (
          <div className="text-green-500 text-sm">Payment Successful!</div>
        )}

        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-500"
        >
          {isProcessing ? "Processing..." : `Pay Now $${finalTotal.toFixed(2)}`}
        </button>
      </form>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <h2 className="text-xl font-bold text-green-600">
              Order Successful!
            </h2>
            <p className="mt-4">
              Your payment has been processed successfully. Redirecting to the
              home page...
            </p>
          </div>
        </div>
      )}
    </>
  );
};
const cancelPayment = async (
  orderId,
  clearCart,
  navigate,
  setShowCancelPopup
) => {
  setShowCancelPopup(true);
  setTimeout(async () => {
    clearCart();
    try {
      await axios.put(
        `https://api.theonlysale.com/api/order/update/status/${orderId}`,
        { status: "canceled" }
      );
      navigate("/");
    } catch (err) {
      console.error("Error canceling order: ", err);
    }
  }, 2000);
};
const PaymentPage = () => {
  const { cartItems, calculateTotal, clearCart } = useCart();
  const [deliveryFee] = useState(10.0);
  const total = calculateTotal();
  const finalTotal = total + deliveryFee;
  const navigate = useNavigate();
  const [showCancelPopup, setShowCancelPopup] = useState(false);

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const amount = searchParams.get("amount");
  const [arrivalDate, setArrivalDate] = useState(null);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate("/cart");
    }

    // Fetch order details to get arrivalDate
    const fetchOrderDetails = async () => {
      try {
        const response = await axios.get(
          `https://api.theonlysale.com/api/order/get/${orderId}`
        );
        const orderData = response.data;
        setArrivalDate(orderData.arrivalDate); // Set the arrival date from the order
      } catch (err) {
        console.error("Error fetching order details: ", err);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    }
  }, [cartItems, navigate, orderId]);

  return (
    <>
      <ToastContainer />

      <Header />
      <section className="bg-white py-8 dark:bg-gray-900">
        <div className="mx-auto max-w-screen-xl px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Order Summary
              </h2>
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow"
                  >
                    <div className="flex items-center">
                      <img
                        className="h-16 w-16 rounded-md"
                        src={item.imageURIs[0]}
                        alt={item.name}
                      />
                      <div className="ml-4">
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Quantity: {item.quantity}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-base font-medium text-gray-900 dark:text-white">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Display Arrival Date */}
              {arrivalDate && (
                <div className="mt-4 bg-blue-100 text-blue-700 p-4 rounded-lg">
                  <p className="text-sm">
                    <strong>Estimated Arrival Date:</strong>{" "}
                    {new Date(arrivalDate).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="mt-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <p className="text-base font-normal text-gray-500 dark:text-gray-400">
                    Subtotal
                  </p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    ${total.toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-base font-normal text-gray-500 dark:text-gray-400">
                    Delivery Fee
                  </p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    ${deliveryFee.toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    Total
                  </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    ${finalTotal.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Complete Your Payment
              </h2>
              <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Elements stripe={stripePromise}>
                  <PaymentForm finalTotal={finalTotal} />
                </Elements>

                <button
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-lg mt-4 hover:bg-red-700"
                  onClick={() =>
                    cancelPayment(
                      orderId,
                      clearCart,
                      navigate,
                      setShowCancelPopup
                    )
                  }
                >
                  Cancel Payment
                </button>

                {showCancelPopup && (
                  <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                      <h2 className="text-xl font-bold text-red-600">
                        Order Canceled!
                      </h2>
                      <p className="mt-4">
                        Your payment has been canceled and you will be
                        redirected to the home page...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default PaymentPage;
